import React, { useState, useRef } from 'react';
import { ArrowRightIcon, StarIcon, PlayIcon, PauseIcon } from 'lucide-react';
import { AudioPlayer } from './AudioPlayer';
const MobileAudioButton: React.FC<{
  src: string;
}> = ({
  src
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };
  const handleEnded = () => setIsPlaying(false);
  return <>
      <audio ref={audioRef} src={src} preload="none" onEnded={handleEnded} data-id="element-234" />
      <button type="button" onClick={toggle} className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-orange-600 to-orange-500 hover:from-orange-500 hover:to-yellow-500 flex items-center justify-center transition-all duration-300 shadow-lg ${isPlaying ? 'shadow-orange-500/60 scale-95' : 'shadow-orange-600/30'}`} aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-235">
        {isPlaying ? <PauseIcon className="h-5 w-5 text-white" data-id="element-236" /> : <PlayIcon className="h-5 w-5 text-white ml-0.5" data-id="element-237" />}
      </button>
    </>;
};
export const HeroSection = () => {
  return <section className="py-3 md:py-8 relative overflow-hidden" data-id="element-238">
      <div className="absolute inset-0 bg-bttf-highlight/10 mix-blend-overlay" data-id="element-239"></div>
      <div className="container mx-auto px-4 flex flex-col md:flex-row items-center md:items-start relative z-10 gap-4 md:gap-8 lg:gap-12" data-id="element-240">
        {/* Left Column - Content */}
        <div className="w-full md:w-1/2 flex flex-col justify-between" data-id="element-241">
          <h1 className="text-[2.25rem] leading-[1.15] md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-2.5 md:mb-6 font-future tracking-tight bg-gradient-main text-transparent bg-clip-text pb-2" data-id="element-242">
            It Wasn't Just A Movie. And You've Always Known That.
          </h1>

          <p className="text-[15px] md:text-lg lg:text-xl text-white/90 leading-relaxed mb-3.5 md:mb-6 font-body font-light italic" data-id="element-243">
            If you've watched it dozens of times. If you quote it without
            thinking. If you hear 'The Power of Love' and your heart skips a
            beat, this book captures what you've always felt but never read.
          </p>

          {/* Mobile Image */}
          <div className="md:hidden flex justify-center mb-3" data-id="element-244">
            <div className="relative w-[90%]" data-id="element-245">
              <div className="absolute -inset-1 bg-gradient-main rounded-lg blur-md opacity-30" data-id="element-246"></div>
              <div className="relative bg-bttf-bg rounded-lg overflow-hidden shadow-xl border border-bttf-accent/30" style={{
              aspectRatio: '1/1'
            }} data-id="element-247">
                <img src="https://cdn.magicpatterns.com/uploads/hJvxVoHhP1Zt5TJuKs2tSR/PH.02.png" srcSet="https://cdn.magicpatterns.com/uploads/hJvxVoHhP1Zt5TJuKs2tSR/PH.02.png?w=400 400w, https://cdn.magicpatterns.com/uploads/hJvxVoHhP1Zt5TJuKs2tSR/PH.02.png?w=600 600w" sizes="(max-width: 768px) 90vw, 400px" alt="Back In Time: The Untold Story - 40th Anniversary Collector's Edition" className="w-full h-full object-cover" width="400" height="400" fetchpriority="high" decoding="async" data-id="element-248" />
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mb-3 md:mb-6" data-id="element-249">
            <p className="text-xs md:text-sm text-bttf-subtext mb-2 text-center md:text-left italic font-body" data-id="element-250">
              For collectors. For dreamers. For the generation that grew up at
              88 MPH.
            </p>
            <a href="#pricing-top" className="inline-flex items-center bg-bttf-primary hover:bg-bttf-primary-hover text-bttf-text font-bold px-6 py-3 md:px-8 md:py-4 rounded-md text-base md:text-lg transition-all transform hover:scale-105 shadow-md hover:shadow-neon-orange w-full md:w-fit justify-center font-body" data-id="element-251">
              Claim My Collector's Edition →
            </a>
          </div>

          {/* Desktop Audio Player */}
          <div className="hidden md:block bg-gradient-to-br from-gray-900/60 to-gray-800/40 border border-orange-500/30 px-4 py-3 rounded-lg backdrop-blur-sm shadow-[0_0_15px_rgba(255,108,0,0.15)] mb-6" data-id="element-252">
            <div className="flex items-center gap-2 mb-2" data-id="element-253">
              <span className="text-base" data-id="element-254">🎧</span>
              <span className="text-sm font-semibold text-orange-200 font-body" data-id="element-255">
                Press Play: Hear How It All Began (1 min preview).
              </span>
            </div>
            <AudioPlayer src="https://cdn.shopify.com/s/files/1/0586/4069/5378/files/BIT_Audiobook_Preview.mp3?v=1762440014" data-id="element-256" />
            <p className="text-xs text-gray-400 mt-2 text-center font-body" data-id="element-257">
              Hear the ticking clocks that started everything.
            </p>
          </div>

          {/* Mobile Audio Player — single play button */}
          <div className="md:hidden flex items-center gap-3 bg-gradient-to-br from-gray-900/60 to-gray-800/40 border border-orange-500/30 px-3 py-2.5 rounded-lg backdrop-blur-sm mb-3 shadow-[0_0_15px_rgba(255,108,0,0.15)]" data-id="element-258">
            <span className="text-base" data-id="element-259">🎧</span>
            <span className="text-xs font-semibold text-orange-200 font-body flex-1" data-id="element-260">
              Press Play: Hear How It All Began (1 min preview).
            </span>
            <MobileAudioButton src="https://cdn.shopify.com/s/files/1/0586/4069/5378/files/BIT_Audiobook_Preview.mp3?v=1762440014" data-id="element-261" />
          </div>

          <div className="border-t-2 border-bttf-accent/20 pt-4 mb-4 md:pt-6 md:mb-6" data-id="element-262">
            <p className="text-xs md:text-sm text-[#b3b3b3] text-center md:text-left leading-relaxed font-body" data-id="element-263">
              190+ pages of production history, cultural analysis, and untold
              stories from cinema's most beloved time travel film.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 md:gap-3 justify-center md:justify-start" data-id="element-264">
            <div className="inline-block bg-bttf-primary/20 text-bttf-primary border border-bttf-primary/30 font-bold px-2.5 py-1 md:px-3 md:py-1.5 rounded-md whitespace-nowrap text-xs md:text-sm animate-pulse shadow-[0_0_15px_rgba(255,108,0,0.5)] font-body" data-id="element-265">
              🏆 BEST SELLER
            </div>
            <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0" data-id="element-266">
              <div className="flex items-center gap-0.5" data-id="element-267">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className="h-3.5 w-3.5 md:h-4 md:w-4 text-[#FFD300] fill-[#FFD300]" data-id="element-268" />)}
              </div>
              <span className="text-xs md:text-sm font-bold text-bttf-text font-body" data-id="element-269">
                4.9
              </span>
              <span className="text-[10px] md:text-xs text-bttf-subtext whitespace-nowrap font-body" data-id="element-270">
                (5,354+ Reviews)
              </span>
            </div>
          </div>
        </div>

        {/* Right Column - Desktop Image */}
        <div className="hidden md:flex md:w-1/2 justify-center items-start" data-id="element-271">
          <div className="relative w-full max-w-xl" data-id="element-272">
            <div className="absolute -inset-1 bg-gradient-main rounded-lg blur-md opacity-30" data-id="element-273"></div>
            <div className="relative bg-bttf-bg rounded-lg overflow-hidden shadow-xl border border-bttf-accent/30" style={{
            aspectRatio: '1/1'
          }} data-id="element-274">
              <img src="https://cdn.magicpatterns.com/uploads/hJvxVoHhP1Zt5TJuKs2tSR/PH.02.png" srcSet="https://cdn.magicpatterns.com/uploads/hJvxVoHhP1Zt5TJuKs2tSR/PH.02.png?w=600 600w, https://cdn.magicpatterns.com/uploads/hJvxVoHhP1Zt5TJuKs2tSR/PH.02.png?w=800 800w" sizes="(max-width: 1024px) 50vw, 600px" alt="Back In Time: The Untold Story - 40th Anniversary Collector's Edition" className="w-full h-full object-cover" width="600" height="600" fetchpriority="high" decoding="async" data-id="element-275" />
            </div>
          </div>
        </div>
      </div>
    </section>;
};